import { AppShell, Box, Center, NavLink, Stack, Text } from '@mantine/core';
import {
  Link,
  LoaderFnContext,
  Outlet,
  createFileRoute,
  useMatchRoute,
} from '@tanstack/react-router';

import {
  IconBuilding,
  IconBuildings,
  IconCloudLock,
  IconDashboard,
  IconReportMoney,
  IconUsersGroup,
} from '@tabler/icons-react';
import { logger } from '@/lib/logger';
import { MyRouterContext } from '@/App';
import { isAdmin } from '@/lib/auth';
import { ExternalNavLinks } from '@/components/ExternalNavLinks';
import { MainContainer } from '@/components/MainContainer';
import { NavbarBrand } from '@/components/NavbarBrand';
import { PageHeader } from '@/components/PageHeader';
import { notifyError } from '@/lib/notification';

export const Route = createFileRoute('/_main/tenant')({
  loader,
  component: Component,
});

async function loader({
  context: { userInfo, adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const tenant = await adminClient.settings_v1_get_tenant();
  return {
    tenant,
    userInfo,
    adminClient,
  };
}

const iconProps = {
  size: '1.4em',
  stroke: 1.5,
};

function Component() {
  const { userInfo, tenant } = Route.useLoaderData();

  if (!isAdmin(userInfo)) {
    return (
      <>
        <AppShell.Navbar p="md" />
        <AppShell.Main>
          <MainContainer>
            <PageHeader title="権限がありません。" />
          </MainContainer>
        </AppShell.Main>
      </>
    );
  }

  return (
    <>
      <AppShell.Navbar p="md">
        <Stack gap="0" justify="space-between" h="100%">
          <Box>
            <NavbarBrand />
            <NavbarContents />
          </Box>
          <ExternalNavLinks />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        {tenant.spec.status === 'delete_scheduled' && (
          <Box h="47px" bg="yellow" c="black">
            <Center h="100%">
              <Text size="sm" ta="center">
                今月末でサブスクリプションが解約されます。解約と同時にテナントが削除され、利用できなくなります。
                <br />
                引き続きご利用される場合、および設定を維持したい場合は、サブスクリプションの継続をお願いいたします。
              </Text>
            </Center>
          </Box>
        )}
        <Box
          mih={
            tenant.spec.status === 'delete_scheduled'
              ? 'calc(100dvh - 94px)'
              : 'calc(100dvh - 47px)'
          }
        >
          <Outlet />
        </Box>
      </AppShell.Main>
    </>
  );
}

export default function NavbarContents() {
  const { adminClient, tenant } = Route.useLoaderData();
  const matchRoute = useMatchRoute();

  const redirectToBillingPortal = async () => {
    try {
      const res = await adminClient.admin_v1_create_billing_portal_session(undefined);
      window.location.href = res.url;
    } catch (e) {
      logger.error(e);
      notifyError('請求管理ページへのアクセスに失敗しました。');
    }
  };

  return (
    <>
      <AppShell.Section>
        <div className="text-xs m-3 font-bold text-plamo-text-secondary">テナント</div>
      </AppShell.Section>
      <NavLink
        component={Link}
        to="/tenant/projects"
        active={!!matchRoute({ to: '/tenant/projects' })}
        label="プロジェクト管理"
        leftSection={<IconBuilding {...iconProps} />}
      />
      <NavLink
        component={Link}
        to="/tenant/users"
        // 招待の送信 (/tenant/users/send-invitation) も「ユーザ管理」配下として示す
        active={!!matchRoute({ to: '/tenant/users', fuzzy: true })}
        label="ユーザ管理"
        leftSection={<IconUsersGroup {...iconProps} />}
      />
      <NavLink
        component={Link}
        to="/tenant/usage"
        active={!!matchRoute({ to: '/tenant/usage' })}
        label="利用状況"
        leftSection={<IconDashboard {...iconProps} />}
      />
      <NavLink
        component={Link}
        to="/tenant/limits"
        active={!!matchRoute({ to: '/tenant/limits' })}
        label="利用上限設定"
        leftSection={<IconCloudLock {...iconProps} />}
      />
      <NavLink
        component={Link}
        to="/tenant/settings"
        active={!!matchRoute({ to: '/tenant/settings' })}
        label="テナント設定"
        leftSection={<IconBuildings {...iconProps} />}
      />
      {tenant.spec.customer_id && (
        <NavLink
          onClick={redirectToBillingPortal}
          label="請求管理"
          leftSection={<IconReportMoney {...iconProps} />}
        />
      )}
    </>
  );
}
